import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { AsyncSelectInputSingle } from '@frontend/basic-forms';
import React from 'react';

import useObjectSelectInput from './object-select-input.controller';

const ID = 'object-select-input';

export interface ObjectSelectInputProps<T extends { id: string }, D extends string | number> {
    id?: string;
    label: React.ReactNode;
    required?: boolean;
    submitted: boolean;
    helpMessage?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    hideOnOneOption?: boolean;
    useConditionedStyling?: boolean;
    isClearable?: boolean;
    //TODO: createEnabled?: boolean;

    /**
     * @description Will check the current URL for a path param that matches this value.
     * If found (using the fetch function) the select will be disabled and the value will be set.
     * Otherwise the select will function as usual.
     * Do not use with useQueryParam (may cause unexpected behaviour)
     */
    useUrlOverwrite?: UrlParams<T>;
    /**
     * @description Will check the current URL for a query param that matches this value.
     * If found (using the fetch function) the value will be set.
     * When selecting a new value, the URL will be updated.
     * Do not use with useUrlOverwrite (may cause unexpected behaviour)
     */
    useQueryParam?: UrlParams<T>;
    onChange?: (value: T | null) => void;
    idValue?: string | null;
    value?: T | null;
    queryParams?: ApiQueryParams<D>;
    fetch: (queryParams?: ApiQueryParams<DefaultQueryParams | D>) => Promise<PaginatedResponse<T>>;
    itemLabel: (item: T) => string;
    disableFetch?: boolean;
    mapOptions?: keyof T;
    onFocus?: () => void;
    onBlur?: () => void;
    subset?: string[]; //array of valid ID's
    hidden?: boolean;
    singleFetch?: (id: string) => Promise<T>;
}

interface UrlParams<T> {
    param: string;
    fetch: (id: string) => Promise<T>;
}

/**
 * @deprecated
 * @param props
 * @returns
 */
export const ObjectSelectInput = <T extends { id: string }, D extends string | number>(props: ObjectSelectInputProps<T, D>) => {
    const viewProps = useObjectSelectInput(props);
    if (viewProps.hide) return <></>;
    return (
        <AsyncSelectInputSingle
            id={props.id || ID}
            label={props.label}
            className={props.className}
            helpMessage={props.helpMessage}
            submitted={props.submitted}
            required={props.required}
            options={viewProps.options}
            onChange={(v: { value: string; label: string }) => viewProps.onChange(v?.value)}
            value={viewProps.value?.id}
            isDisabled={viewProps.overwriteDisabled || props.disabled}
            onMenuScrollToBottom={viewProps.onScrollToBottom}
            loadOptions={viewProps.search}
            isClearable={props.isClearable}
            useConditionedStyling={props.useConditionedStyling}
            onFocusChange={props.onFocus}
            onBlur={props.onBlur}
        />
    );
};

export default ObjectSelectInput;
